import { alpha, FormHelperText, Theme } from '@material-ui/core';
import { ApiInfoRtn } from 'app/core/api/api-core/api-core.types';
import ClassName from 'app/styles/ClassName';
import styledConsts from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import useBodyClass from 'app/tools/hooks/misc-hooks';
import useSafeAreaInsets from 'app/tools/hooks/use-safe-area-insets';
import { Markup } from 'interweave';
import React from 'react';
import { Prompt } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import AutoHeightAnimation from '../frames/animation/AutoHeightAnimation';
import Icon from './icon/Icon';
import IconLib from './icon/icon-lib';
import Body from './typography/body/Body';

export const PadExpand: React.VFC<{
  secondary?: boolean;
  proportion?: number;
}> = ({ secondary, proportion = 1 }) => (
  <div
    className={ClassName.NO_AUTO_MARGIN}
    css={`
      flex: ${secondary ? proportion : proportion * 100} 0 0;
    `}
  />
);

export const PadCollapse: React.VFC<{
  pad: number;
  proportion?: number;
}> = ({ pad, proportion = 1 }) => (
  <div
    className={ClassName.NO_AUTO_MARGIN}
    css={css`
      flex: ${proportion * 10000} 0 0;
      max-height: ${styledFuncs.pad(pad)};
    `}
  >
    &nbsp;
  </div>
);

export const TopBannerText: React.FC<{
  antiMargin?: boolean;
  expandBelow?: boolean;
}> = ({ children, antiMargin = true, expandBelow = true }) => {
  const { top } = useSafeAreaInsets();
  return (
    <>
      <div
        className={ClassName.NO_AUTO_MARGIN}
        css={css`
          text-align: center;
          font-size: 16px;
          background: #1cad1c;
          color: white;
          align-self: stretch;
          padding: 6px;
          padding-top: ${top ? top + 2 : 6}px;
          font-weight: 500;
          margin: ${antiMargin ? styledFuncs.pad(-1) : 0};
        `}
      >
        {children}
      </div>
      {expandBelow && <PadExpand secondary />}
    </>
  );
};

export const MarkupIfString: React.VFC<{
  content: React.ReactNode;
}> = ({ content }) => (
  <>
    {typeof content === 'string' ? (
      <Markup noWrap {...{ content }} />
    ) : (
      content
    )}
  </>
);

export const AnimatedMarkup: React.FC = ({ children }) => (
  <AutoHeightAnimation updateOn={children ?? null}>
    <MarkupIfString content={children} />
  </AutoHeightAnimation>
);

export const ErrorText: React.VFC<{
  message?: string | ApiInfoRtn;
}> = ({ message }) => {
  const errorText =
    typeof message === 'string' ? message : message?.detail;
  return (
    <FormHelperText
      component="div"
      error
      className={ClassName.NO_HEIGHT_MARGIN}
      css={css`
        margin: ${errorText ? styledFuncs.pad() : '0px'};
        transition: ${styledFuncs.createTransition([
          'margin-bottom',
        ])};
      `}
    >
      <AnimatedMarkup>{errorText}</AnimatedMarkup>
    </FormHelperText>
  );
};

export const InputTitle = styled(Body).attrs({
  withNext: true,
  bold: true,
})`
  align-self: stretch;
  text-align: start;
`;

export const PromptUnsaved: React.VFC<{
  when: boolean;
}> = ({ when }) => (
  <Prompt
    message="Leaving this page will discard any unsubmitted changes. Continue anyway?"
    {...{ when }}
  />
);

export const DefaultUserImg: React.VFC<{
  size?: number;
  className?: string;
}> = ({ size = 80, className }) => (
  <div
    css={css`
      background-color: ${p => alpha(styledConsts.cPrimary(p), 0.4)};
      ${styledMixins.roundCorners}
      display: flex;
      justify-content: center;
      align-items: center;
      height: ${size}px;
      width: ${size}px;
      min-width: ${size * 0.8}px;
      align-items: center;
    `}
    {...{ className }}
  >
    <Icon
      icon={IconLib.FILLED_USER}
      color="primary"
      size={size * 0.6}
    />
  </div>
);

export const InsetAreaSpacer: React.VFC<{
  pos: 'top' | 'bottom';
}> = ({ pos }) => {
  const insets = useSafeAreaInsets();
  const marginCss = (p: { theme: Theme }) =>
    `margin-${pos}: ${styledFuncs.pad(-1)(p)};`;

  return (
    <div
      className={ClassName.NO_AUTO_MARGIN}
      css={css`
        height: ${insets[pos] || 0}px;
        ${insets[pos] && marginCss}
      `}
    />
  );
};

export const StylePageBody: React.VFC<{ className?: string }> = ({
  className,
}) => {
  useBodyClass(className || []);
  return null;
};

export const PaleOverlay = styled.div`
  position: absolute;
  ${styledMixins.fullsize};
  background-color: #ffffff8f;
  z-index: 3;
`;
